/**
 * Maps a router pathname to its canonical screen name for `page()` calls.
 * Page views are only emitted for signed-in, non-staff users (see segment.ts
 * `shouldTrack`), so `/login`, `/admin/*` and the dev-only `/__dev/*` routes are
 * deliberately not listed here.
 *
 * Dynamic routes are matched by pattern (e.g. `/topics/abc-123` →
 * `Topic Detail`). Static paths take precedence over patterns, and an
 * unmatched path resolves to a documented default rather than being silent.
 */

/** Screen name used when a path matches no known route (never silent). */
export const UNKNOWN_SCREEN_NAME = "Unknown";

// Exact, static routes. Checked first so e.g. `/reports/templates` wins over
// the `/reports/:reportId` pattern below.
const STATIC_SCREEN_NAMES: Record<string, string> = {
  "/": "Dashboard",
  "/data-bank": "Data Bank",
  "/data-bank/feedback": "Feedback Vault",
  "/data-bank/feedback/recordings": "Data Bank Recordings",
  "/data-bank/context": "Context Vault",
  "/data-bank/filters": "Filters Vault",
  "/topics": "Topics",
  "/overview": "Overview",
  "/map": "Map",
  "/reports": "Evidence Builder",
  "/reports/templates": "Extract Templates",
  "/chat": "Ask",
  "/emotions": "Emotions",
  "/trends": "Trends",
  "/strategies": "Strategies",
  "/recordings": "Recordings",
};

// Parameterised routes, matched in order after static routes.
const DYNAMIC_SCREEN_NAMES: ReadonlyArray<{ pattern: RegExp; name: string }> = [
  { pattern: /^\/data-bank\/files\/[^/]+$/, name: "Data Bank File" },
  { pattern: /^\/data-bank\/feedback\/folders\/[^/]+$/, name: "Feedback Vault" },
  { pattern: /^\/data-bank\/context\/folders\/[^/]+$/, name: "Context Vault" },
  { pattern: /^\/data-bank\/filters\/(?:dimensions|demographics)(?:\/[^/]+)?$/, name: "Filters Vault" },
  { pattern: /^\/topics\/[^/]+\/analysis$/, name: "Topic Detail Analysis" },
  { pattern: /^\/topics\/[^/]+$/, name: "Topic Detail" },
  { pattern: /^\/overview\/[^/]+$/, name: "Overview Category" },
  { pattern: /^\/overview\/[^/]+\/[^/]+$/, name: "Overview Subcategory" },
  { pattern: /^\/reports\/[^/]+$/, name: "Extract Detail" },
];

/** Strip a trailing slash (except for the root path). */
function normalisePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/** Resolve a router pathname to its canonical screen name. */
export function screenNameFor(pathname: string): string {
  const path = normalisePath(pathname);

  const staticName = STATIC_SCREEN_NAMES[path];
  if (staticName) {
    return staticName;
  }

  for (const { pattern, name } of DYNAMIC_SCREEN_NAMES) {
    if (pattern.test(path)) {
      return name;
    }
  }

  return UNKNOWN_SCREEN_NAME;
}
